import { useState } from 'react'
import type { FormEvent } from 'react'
import { sucursales as apiSucursales } from '../api/servicios'
import type { Sucursal } from '../api/tipos'
import { Dialogo } from '../componentes/Dialogo'
import { Estado } from '../componentes/Estado'
import { mensajeDeError, usarCargar } from '../componentes/usarCargar'
import './listados.css'

type Ventana = { tipo: 'ninguno' } | { tipo: 'nueva' } | { tipo: 'editar'; sucursal: Sucursal }

export function Sucursales() {
    const [ventana, setVentana] = useState<Ventana>({ tipo: 'ninguno' })

    // Se listan tambien las inactivas: si no, una sucursal cerrada desaparece de
    // la pantalla y no hay forma de volver a abrirla desde el sistema.
    const lista = usarCargar(() => apiSucursales.listar(false), [])

    const items = lista.datos ?? []
    const activas = items.filter((s) => s.activo).length

    function refrescar() {
        setVentana({ tipo: 'ninguno' })
        lista.recargar()
    }

    return (
        <div className="pagina-listado columna g4">
            <header className="fila entre envuelve g3">
                <h1>Sucursales</h1>

                <button
                    type="button"
                    className="boton boton-primario"
                    onClick={() => setVentana({ tipo: 'nueva' })}
                >
                    Nueva sucursal
                </button>
            </header>

            {activas > 1 && (
                <div className="aviso aviso-atencion">
                    Con más de una sucursal activa, cada usuario necesita tener la suya asignada
                    en <strong>Usuarios</strong>. Quien no la tenga no va a poder abrir caja ni
                    vender.
                </div>
            )}

            <section className="panel columna g3">
                <Estado
                    cargando={lista.cargando}
                    error={lista.error}
                    alReintentar={lista.recargar}
                    vacio={items.length === 0}
                    mensajeVacio="No hay ninguna sucursal configurada."
                >
                    <div className="tabla-envoltura">
                        <table className="tabla">
                            <thead>
                                <tr>
                                    <th>Sucursal</th>
                                    <th className="oculto-angosto">Dirección</th>
                                    <th className="oculto-angosto derecha">Usuarios</th>
                                    <th />
                                </tr>
                            </thead>
                            <tbody>
                                {items.map((s) => (
                                    <tr key={s.sucursalId}>
                                        <td>
                                            <div className="columna">
                                                <span>{s.nombre}</span>
                                                <span className="tenue diminuto numero">
                                                    {s.codigo}
                                                    {!s.activo && ' · inactiva'}
                                                    {s.cierreCiego && ' · cierre ciego'}
                                                </span>
                                            </div>
                                        </td>
                                        <td className="oculto-angosto chico tenue">
                                            {s.direccion ?? '—'}
                                        </td>
                                        <td className="oculto-angosto derecha numero">
                                            {s.usuariosAsignados}
                                        </td>
                                        <td className="derecha">
                                            <button
                                                type="button"
                                                className="boton boton-fantasma"
                                                onClick={() =>
                                                    setVentana({ tipo: 'editar', sucursal: s })
                                                }
                                            >
                                                Editar
                                            </button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </Estado>
            </section>

            {ventana.tipo !== 'ninguno' && (
                <DialogoSucursal
                    sucursal={ventana.tipo === 'editar' ? ventana.sucursal : null}
                    activas={activas}
                    alGuardar={refrescar}
                    alCerrar={() => setVentana({ tipo: 'ninguno' })}
                />
            )}
        </div>
    )
}

/* -------------------------------------------------------------------------- */

interface PropsSucursal {
    sucursal: Sucursal | null
    activas: number
    alGuardar: () => void
    alCerrar: () => void
}

function DialogoSucursal({ sucursal, activas, alGuardar, alCerrar }: PropsSucursal) {
    const [codigo, setCodigo] = useState(sucursal?.codigo ?? '')
    const [nombre, setNombre] = useState(sucursal?.nombre ?? '')
    const [direccion, setDireccion] = useState(sucursal?.direccion ?? '')
    const [cierreCiego, setCierreCiego] = useState(sucursal?.cierreCiego ?? true)
    const [activo, setActivo] = useState(sucursal?.activo ?? true)
    const [error, setError] = useState<string | null>(null)
    const [enviando, setEnviando] = useState(false)

    async function guardar(evento: FormEvent) {
        evento.preventDefault()
        setError(null)

        if (nombre.trim().length === 0) {
            setError('El nombre es obligatorio.')
            return
        }

        if (!sucursal && codigo.trim().length === 0) {
            setError('El código es obligatorio.')
            return
        }

        setEnviando(true)

        try {
            if (sucursal) {
                await apiSucursales.actualizar(sucursal.sucursalId, {
                    nombre: nombre.trim(),
                    direccion: direccion.trim() || undefined,
                    cierreCiego,
                    activo,
                })
            } else {
                await apiSucursales.crear({
                    codigo: codigo.trim(),
                    nombre: nombre.trim(),
                    direccion: direccion.trim() || undefined,
                    cierreCiego,
                })
            }

            alGuardar()
        } catch (fallo) {
            setError(mensajeDeError(fallo))
        } finally {
            setEnviando(false)
        }
    }

    return (
        <Dialogo
            titulo={sucursal ? 'Editar sucursal' : 'Nueva sucursal'}
            alCerrar={alCerrar}
            acciones={
                <>
                    <button type="button" className="boton" onClick={alCerrar}>
                        Cancelar
                    </button>
                    <button
                        type="submit"
                        form="forma-sucursal"
                        className="boton boton-primario"
                        disabled={enviando}
                    >
                        {enviando ? 'Guardando…' : 'Guardar'}
                    </button>
                </>
            }
        >
            <form id="forma-sucursal" className="columna g4" onSubmit={guardar}>
                {!sucursal && activas >= 1 && (
                    <div className="aviso aviso-atencion">
                        Al crear la segunda sucursal, los usuarios que no tengan una asignada
                        dejan de poder operar hasta que se les asigne.
                    </div>
                )}

                {!sucursal && (
                    <div>
                        <label className="etiqueta" htmlFor="codigo-sucursal">
                            Código
                        </label>
                        <input
                            id="codigo-sucursal"
                            className="campo numero"
                            maxLength={20}
                            autoComplete="off"
                            value={codigo}
                            // El backend exige mayusculas; se convierte al
                            // escribir para no rechazar el formulario por eso.
                            onChange={(e) => setCodigo(e.target.value.toUpperCase())}
                        />
                        <p className="tenue diminuto" style={{ marginTop: 'var(--e1)' }}>
                            Corto y sin espacios, por ejemplo CENTRO. No se puede cambiar después.
                        </p>
                    </div>
                )}

                <div>
                    <label className="etiqueta" htmlFor="nombre-sucursal">
                        Nombre
                    </label>
                    <input
                        id="nombre-sucursal"
                        className="campo"
                        maxLength={120}
                        value={nombre}
                        onChange={(e) => setNombre(e.target.value)}
                    />
                </div>

                <div>
                    <label className="etiqueta" htmlFor="direccion-sucursal">
                        Dirección <span className="tenue">(opcional)</span>
                    </label>
                    <input
                        id="direccion-sucursal"
                        className="campo"
                        maxLength={250}
                        value={direccion}
                        onChange={(e) => setDireccion(e.target.value)}
                    />
                </div>

                <div className="columna g1">
                    <label className="fila g2 interruptor">
                        <input
                            type="checkbox"
                            checked={cierreCiego}
                            onChange={(e) => setCierreCiego(e.target.checked)}
                        />
                        <span>Cierre ciego</span>
                    </label>
                    <p className="tenue diminuto">
                        El cajero no ve el efectivo esperado hasta declarar lo que contó.
                    </p>
                </div>

                {sucursal && (
                    <div className="columna g1">
                        <label className="fila g2 interruptor">
                            <input
                                type="checkbox"
                                checked={activo}
                                onChange={(e) => setActivo(e.target.checked)}
                            />
                            <span>Activa</span>
                        </label>
                        <p className="tenue diminuto">
                            Una sucursal inactiva no admite ventas ni caja, pero su historial se
                            conserva. Hay que reasignar a su gente antes de desactivarla.
                        </p>
                    </div>
                )}

                {error && (
                    <div className="aviso aviso-error" role="alert">
                        {error}
                    </div>
                )}
            </form>
        </Dialogo>
    )
}
